import React from 'react';
import { PlusIcon } from '@heroicons/react/solid';
import Button from 'components/atoms/Button';
import Spinner from 'components/atoms/Spinner';
import TabLink from 'components/atoms/TabLink';
import IncidentSearchBar from 'components/molecules/IncidentSearchBar';
import InPageSearch from 'components/molecules/InPageSearch';
import SlideOver from 'components/molecules/SlideOver';
import CommentPanel from 'components/organisms/CommentPanel';
import IncidentPanel from 'components/organisms/IncidentPanel';
import Table from 'components/organisms/Table';
import MainLayoutV2 from 'components/templates/MainLayout/MainLayoutV2';
import { useEffect, useState } from 'react';
import { useGetPermissionsMutation } from 'redux/api/vehicleIncidents';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
	useCreateIncidentActivityMutation,
	useCreateIncidentMutation,
	useDeleteIncidentActivityMutation,
	useGetIncidentActivityMutation,
	useGetIncidentMutation,
	useGetVehicleIncidentsMutation,
	VehiclePagination,
	useUpdateIncidentActivityMutation,
} from 'redux/api/vehicleIncidents';
import { useAppDispatch, useAppSelector, useQuery } from 'redux/hooks';
import {
	closeCommentModal,
	closeIncidentModal,
	openIncidentModal,
	updateIncidentActionState,
	updateBasicPermissions,
} from 'redux/slices/vehicleIncidentsSlice';
import { IQueryPeriod } from 'types/VehicleIncidents';
import { hasAccess } from 'utils/permissions';
import * as XLSX from 'xlsx';
import ExportIcon from '../assets/icons/ExportIcon';
import SideBarIcon from '../assets/icons/SideBarIcon';
import { IVehicleIncident } from 'types/VehicleIncidents';
import { toast } from 'react-toastify';

const Incidents = () => {
	const userPermission = useAppSelector((state) => state.user.permissions);
	const userId = useAppSelector((state) => state.user.id);

	const isAdminManager = hasAccess(userPermission, 'admin-manager');
	const defaultPeriod = isAdminManager ? 'today' : 'all';
	const defaultFilters = isAdminManager
		? 'period=today&status=new'
		: 'period=all';
	const defaultPageSize = 15;

	const [period, setPeriod] = useState<IQueryPeriod>(defaultPeriod);
	const [filters, setFiltersRaw] = useState<string>(
		`${defaultFilters}&page=1&pageSize=${defaultPageSize}`
	);
	const [open, setOpen] = useState(false);
	const [getIncidents, { data, isSuccess, isLoading: isListLoading }] =
		useGetVehicleIncidentsMutation();
	const [
		getIncidentsCloseModal,
		{ data: dataCloseModal, isSuccess: isSuccessCloseModal },
	] = useGetVehicleIncidentsMutation();
	const [getIncidentsForExport] = useGetVehicleIncidentsMutation();
	const IncidentState = useAppSelector((state) => state.vehicleIncident);
	const dispatch = useAppDispatch();
	const [createIncidentActivity] = useCreateIncidentActivityMutation();
	const [deleteIncidentActivity] = useDeleteIncidentActivityMutation();
	const [updateIncidentActivity] = useUpdateIncidentActivityMutation();
	const [getPermissions, { data: permissionsData }] =
		useGetPermissionsMutation();
	const [createIncident, { isLoading }] = useCreateIncidentMutation();
	const [getIncident] = useGetIncidentMutation();
	const [getIncidentActivity, { data: activityData }] =
		useGetIncidentActivityMutation();
	const { uid, action } = useParams();
	const query = useQuery();
	const navigate = useNavigate();
	const [previousReportOpen, setPreviousReportOpen] = useState<boolean>(false);
	const [searchParams] = useSearchParams();
	const incidentFilters = useAppSelector(
		(state) => state.vehicleIncident.filters
	);
	const [currentTableData, setCurrentTableData] = useState<IVehicleIncident[]>(
		[]
	);
	const [hasLoadedList, setHasLoadedList] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(defaultPageSize);
	const [pagination, setPagination] = useState<VehiclePagination>({
		page: 1,
		pageSize: defaultPageSize,
		pageCount: 1,
		total: 0,
	});
	const [isCsvExporting, setIsCsvExporting] = useState(false);
	const pageSizeOptions = [15, 25, 50, 100];
	// @ts-ignore
	const nuisanceFilter = incidentFilters?.status?.find((item) => {
		return item.title === 'nuisance';
	});
	// @ts-ignore
	const duplicateFilter = incidentFilters?.status?.find((item) => {
		return item.title === 'duplicate';
	});

	const includeDuplicateInCsv = () => {
		return duplicateFilter?.checked;
	};

	const includeNuisanceInCsv = () => {
		return nuisanceFilter?.checked;
	};

	const setFilters = (
		newFilters: string,
		page: number = 1,
		nextPageSize: number = pageSize
	) => {
		const searchParams = new URLSearchParams(newFilters || '');
		searchParams.delete('page');
		searchParams.delete('pageSize');
		searchParams.delete('prepend');

		searchParams.set('page', String(page));
		searchParams.set('pageSize', String(nextPageSize));

		setCurrentPage(page);
		setPageSize(nextPageSize);
		setFiltersRaw(searchParams.toString());
	};

	useEffect(() => {
		if (uid) {
			if (action?.toLowerCase() === 'view') {
				dispatch(updateIncidentActionState('view'));
			}
			setFilters(`uid=${uid}`);
		}
		// eslint-disable-next-line
	}, [uid]);

	useEffect(() => {
		if (query && query.get('view') && !previousReportOpen) {
			dispatch(updateIncidentActionState('view'));
			setFilters(`id=${query.get('view')}`);
		}
		// eslint-disable-next-line
	}, [query]);

	useEffect(() => {
		if (permissionsData) {
			dispatch(updateBasicPermissions(permissionsData));
		}
		// eslint-disable-next-line
	}, [permissionsData]);

	useEffect(() => {
		getPermissions();
		// eslint-disable-next-line
	}, [getPermissions]);

	useEffect(() => {
		if (
			searchParams.get('branch_name') ||
			searchParams.get('category') ||
			searchParams.get('status') ||
			searchParams.get('dateFrom') ||
			searchParams.get('dateTo') ||
			searchParams.get('severity') ||
			searchParams.get('correctiveActionsPriority') ||
			searchParams.get('correctiveActions') ||
			searchParams.get('incidentMechanism') ||
			searchParams.get('incidentAgency') ||
			searchParams.get('partOfBody') ||
			searchParams.get('injuryType')
		) {
			const filterObj = {};

			if (searchParams.get('branch_name')) {
				//@ts-ignore
				filterObj.branch = searchParams.get('branch_name');
			}

			if (searchParams.get('status')) {
				//@ts-ignore
				filterObj.status = searchParams.get('status');
			}

			if (searchParams.get('category')) {
				//@ts-ignore
				filterObj.incidentCategory = searchParams.get('category');
			}

			if (searchParams.get('dateFrom')) {
				//@ts-ignore
				filterObj.from = searchParams.get('dateFrom');
			}

			if (searchParams.get('dateTo')) {
				//@ts-ignore
				filterObj.to = searchParams.get('dateTo');
			}

			if (searchParams.get('severity')) {
				//@ts-ignore
				filterObj.severity = searchParams.get('severity');
			}

			if (searchParams.get('correctiveActionsPriority')) {
				//@ts-ignore
				filterObj.correctiveActionsPriority = searchParams.get(
					'correctiveActionsPriority'
				);
			}

			if (searchParams.get('correctiveActions')) {
				//@ts-ignore
				filterObj.correctiveActions = searchParams.get('correctiveActions');
			}

			if (searchParams.get('incidentMechanism')) {
				//@ts-ignore
				filterObj.incidentMechanism = searchParams.get('incidentMechanism');
			}

			if (searchParams.get('incidentAgency')) {
				//@ts-ignore
				filterObj.incidentAgency = searchParams.get('incidentAgency');
			}

			if (searchParams.get('partOfBody')) {
				//@ts-ignore
				filterObj.partOfBody = searchParams.get('partOfBody');
			}

			if (searchParams.get('injuryType')) {
				//@ts-ignore
				filterObj.injuryType = searchParams.get('injuryType');
			}

			setFilters(new URLSearchParams(filterObj).toString());
			setPeriod('all');
		}
		getIncidents(filters);
		// eslint-disable-next-line
	}, [filters, period]);

	useEffect(() => {
		if (isSuccess && data) {
			if (
				IncidentState.action &&
				IncidentState.action === 'view' &&
				data.data.length
			) {
				const selectedIncident = data.data[0];
				const payload = {
					incidentReportId: selectedIncident.id,
					incidentBranchId: selectedIncident.branch.id,
					incidentSection1Id: selectedIncident?.incident_section_1?.id,
					incidentSection2Id: selectedIncident?.incident_section_2?.id,
					incidentSection3Id: selectedIncident?.incident_section_3?.id,
					incidentSection4Id: selectedIncident?.incident_section_4?.id,
					incidentSection5Id: selectedIncident?.incident_section_5?.id,
					incidentSection6Id: selectedIncident?.incident_section_6?.id,
					incidentSection7Id: selectedIncident?.incident_section_7?.id,
					iCamIsVisible:
						selectedIncident?.incident_section_5
							?.incident_investigation_required,
				};
				dispatch(openIncidentModal(payload));
				createIncidentActivity({
					incident: selectedIncident.id,
					user: userId,
					type: 'view report',
					content: 'Report opened',
				});
				dispatch(updateIncidentActionState(null));
			} else {
				setCurrentTableData(data?.data || []);
				setHasLoadedList(true);
				if (data?.pagination) {
					setPagination(data.pagination);
					setCurrentPage(data.pagination.page);
					setPageSize(data.pagination.pageSize);
				}
			}
		}
		// eslint-disable-next-line
	}, [data, isSuccess]);

	useEffect(() => {
		if (isSuccessCloseModal && dataCloseModal) {
			setCurrentTableData(dataCloseModal?.data || []);
			if (dataCloseModal?.pagination) {
				setPagination(dataCloseModal.pagination);
				setCurrentPage(dataCloseModal.pagination.page);
				setPageSize(dataCloseModal.pagination.pageSize);
			}
		}
		// eslint-disable-next-line
	}, [dataCloseModal, isSuccessCloseModal]);

	useEffect(() => {
		if (
			!IncidentState.incidentReportOpen &&
			previousReportOpen &&
			query.get('view')
		) {
			navigate('/incidents');
		} else if (
			IncidentState.incidentReportOpen &&
			!previousReportOpen &&
			!query.get('view') &&
			IncidentState.incidentReportId
		) {
			navigate(`/incidents?view=${IncidentState.incidentReportId}`);
		}
		setPreviousReportOpen(IncidentState.incidentReportOpen);
		// eslint-disable-next-line
	}, [IncidentState.incidentReportOpen]);

	const handleCloseComments = () => {
		dispatch(closeCommentModal());
	};

	const handleCloseIncidentReport = () => {
		dispatch(closeIncidentModal());
		setFilters(filters, currentPage, pageSize);
	};

	const handleOpen = () => {
		setOpen(!open);
	};

	const createNewIncident = async () => {
		//find branch to attach to incident as a default
		const branchIdMap = userPermission?.map((permission) =>
			Number(permission.branch.id)
		);

		//create a new incident
		if (branchIdMap) {
			const newIncident = await createIncident({
				branch: branchIdMap[0],
			});

			const newIncidentId = 'data' in newIncident ? newIncident.data?.id : null;

			if (!newIncidentId) {
				return;
			}

			await getIncident(newIncidentId)
				.unwrap()
				.then((response) => {
					const {
						incident_section_1,
						incident_section_2,
						incident_section_3,
						incident_section_4,
						incident_section_5,
						incident_section_6,
						incident_section_7,
					} = response?.incidentData;

					const updateIncidentStatePayload = {
						incidentReportId: newIncidentId,
						incidentBranchId: branchIdMap[0].toString(),
						incidentSection1Id: incident_section_1.id,
						incidentSection2Id: incident_section_2.id,
						incidentSection3Id: incident_section_3.id,
						incidentSection4Id: incident_section_4.id,
						incidentSection5Id: incident_section_5.id,
						incidentSection6Id: incident_section_6.id,
						incidentSection7Id: incident_section_7.id,
						isNewIncident: true,
					};
					dispatch(openIncidentModal(updateIncidentStatePayload));
				});
		}
	};

	const hasManagerAccess = () => {
		return hasAccess(userPermission, 'manager', 'incident_level', 'write');
	};

	let headings = [];

	if (open) {
		// keep this in case they want it back...
		// headings = ['Branch', 'Date', 'Severity', 'Client', 'Created By', 'Status'];

		headings = [
			'Branch',
			'Incident #',
			'Category',
			'Date',
			'Person Involved',
			'Severity',
			'Client',
			'Created By',
			'Status',
		];
	} else {
		headings = [
			'Branch',
			'Incident #',
			'Category',
			'Date',
			'Person Involved',
			'Severity',
			'Client',
			'Created By',
			'Status',
		];
	}

	if (isLoading) {
		return (
			<MainLayoutV2 url="incidents">
				<Spinner />
			</MainLayoutV2>
		);
	}

	// Full-page spinner only before the first response; later searches keep the search panel mounted.
	if (isListLoading && !hasLoadedList) {
		return (
			<MainLayoutV2 url="incidents">
				<Spinner />
			</MainLayoutV2>
		);
	}

	const extractText: string | number | any = (
		children: string | number | any
	) => {
		if (typeof children === 'string' || typeof children === 'number') {
			return children;
		}

		if (Array.isArray(children)) {
			return children.map((child) => extractText(child)).join(' ');
		}

		//@ts-ignore
		if (React.isValidElement(children) && children.props.children) {
			//@ts-ignore
			return extractText(children.props.children);
		}

		return '';
	};

	const sanitizeForCSV = (value: string | number | any): string => {
		if (typeof value === 'number') return String(value);
		if (typeof value !== 'string') return '';
		return value
			.replace(/"/g, '""')
			.replace(/[\r\n]+/g, ' ')
			.trim();
	};

	const escapeCsvCell = (value: string | number | any): string => {
		return `"${sanitizeForCSV(value)}"`;
	};

	const renderHeadings = () => {
		if (!currentTableData) return [];
		const headings = [
			{
				value: 'Branch',
				alignment: 'left',
			},
			{
				value: 'Incident #',
				alignment: 'left',
			},
			{
				value: 'Category',
				alignment: 'left',
			},
			{
				value: 'Date',
				alignment: 'left',
			},
			{
				value: 'Person Involved',
				alignment: 'left',
			},
			{
				value: 'Severity',
				alignment: 'left',
			},
			{
				value: 'Ticket No.',
				alignment: 'left',
			},
			{
				value: 'Client',
				alignment: 'left',
			},
			{
				value: 'Created By',
				alignment: 'left',
			},
			{
				value: 'Status',
				alignment: 'left',
			},
			{
				value: 'MVI Causation',
				alignment: 'left',
			},
			{
				value: 'Life Saving Rules',
				alignment: 'left',
			},
			{
				value: 'Incident Description',
				alignment: 'left',
			},
		];

		return headings;
	};

	const buildCsvRows = (incidentData: IVehicleIncident[]) => {
		if (!incidentData) return [];
		const rows = incidentData
			.filter((row) => {
				if (!includeDuplicateInCsv() && row.special_status === 'duplicate')
					return false;

				if (!includeNuisanceInCsv() && row.special_status === 'nuisance')
					return false;

				return true;
			})
			.map((row) => {
				return [
					row.branch.name,
					row.id,
					row.incident_section_4?.incident_category &&
					row.incident_section_4?.incident_category.length
						? `${row.incident_section_4.incident_category[0].label}`
						: `No Category`,
					row.incident_section_1?.incident_date
						? new Date(row.incident_section_1?.incident_date)
								.toLocaleDateString('en-AU', {
									weekday: 'short',
									day: 'numeric',
									month: 'short',
								})
								.replace(',', '')
						: 'No date',
					row.incident_section_2?.personInvolved &&
					row.incident_section_2.personInvolved.length
						? `${row.incident_section_2.personInvolved[0].personInvolved_name}`
						: '',
					row.incident_section_4?.severity_rating,
					row.ticket_number,
					row.client,
					row.submitted_by,
					row.special_status ? row.special_status : row.status,
					row.incident_section_4?.mvi_causation
						? row.incident_section_4?.mvi_causation
								?.map((mvi) => mvi.label)
								.join(',')
						: '',
					row.incident_section_4?.life_saving_rule_breach &&
					row.incident_section_4?.life_saving_rules?.length
						? row.incident_section_4?.life_saving_rules
								?.map((rule) => rule.label)
								.join(',')
						: '',
					row.description || '',
				];
			});
		return rows || [];
	};

	const fetchAllIncidentsForCsv = async () => {
		const exportPageSize = 100;
		const searchParams = new URLSearchParams(filters || '');
		searchParams.delete('prepend');
		searchParams.set('page', '1');
		searchParams.set('pageSize', String(exportPageSize));

		const firstResponse = await getIncidentsForExport(
			searchParams.toString()
		).unwrap();
		const allIncidents = [...(firstResponse?.data || [])];
		const totalPages = firstResponse?.pagination?.pageCount || 1;

		for (let page = 2; page <= totalPages; page += 1) {
			searchParams.set('page', String(page));
			const nextResponse = await getIncidentsForExport(
				searchParams.toString()
			).unwrap();
			allIncidents.push(...(nextResponse?.data || []));
		}

		return allIncidents;
	};

	const exportToCSV = async () => {
		setIsCsvExporting(true);
		try {
			const allIncidents = await fetchAllIncidentsForCsv();
			const csvHeaders = renderHeadings().map((heading) =>
				extractText(heading.value)
			);
			const csvRows = buildCsvRows(allIncidents).map((row) =>
				row.map((value) => extractText(value))
			);

			const csvContent = [
				csvHeaders.map((header) => escapeCsvCell(header)).join(','),
				...csvRows.map((row) =>
					row.map((value) => escapeCsvCell(value)).join(',')
				),
			].join('\r\n');

			const csvBlob = new Blob([`\uFEFF${csvContent}`], {
				type: 'text/csv;charset=utf-8;',
			});
			const url = window.URL.createObjectURL(csvBlob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'report.csv';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			toast.error('Failed to export CSV. Please try again.');
		} finally {
			setIsCsvExporting(false);
		}
	};

	const countTotalIncidentsPerCategory = () => {
		const categoryCountMap: { [key: string]: number } = {};

		currentTableData.forEach(
			(incident: { incident_section_4: { incident_category: any[] } }) => {
				incident.incident_section_4?.incident_category?.forEach(
					(category: { label: string | number }) => {
						categoryCountMap[category.label] =
							(categoryCountMap[category.label] || 0) + 1;
					}
				);
			}
		);

		return categoryCountMap;
	};

	const countIncidentsByBranchAndCategory = () => {
		const branchCategoryCountMap: { [key: string]: { [key: string]: number } } =
			{};

		currentTableData.forEach(
			(incident: {
				branch: { name: any };
				incident_section_4: { incident_category: any[] };
			}) => {
				const branchName = incident.branch.name;
				incident.incident_section_4?.incident_category?.forEach((category) => {
					if (!branchCategoryCountMap[branchName]) {
						branchCategoryCountMap[branchName] = {};
					}
					branchCategoryCountMap[branchName][category.label] =
						(branchCategoryCountMap[branchName][category.label] || 0) + 1;
				});
			}
		);

		return branchCategoryCountMap;
	};

	const totalIncidentsPerCategory = countTotalIncidentsPerCategory();
	const incidentsByBranchAndCategory = countIncidentsByBranchAndCategory();

	const exportToExcel = () => {
		const workbook = XLSX.utils.book_new();

		const sheetData = [['Category Report'], ['Category', 'Total Incidents']];
		for (const category in totalIncidentsPerCategory) {
			if (totalIncidentsPerCategory.hasOwnProperty(category)) {
				const totalAsString = String(totalIncidentsPerCategory[category]);
				sheetData.push([category, totalAsString]);
			}
		}
		const sheet = XLSX.utils.aoa_to_sheet(sheetData);

		XLSX.utils.book_append_sheet(workbook, sheet, 'Category Report');

		for (const branch in incidentsByBranchAndCategory) {
			if (incidentsByBranchAndCategory.hasOwnProperty(branch)) {
				const branchData = incidentsByBranchAndCategory[branch];
				const sheetData = [
					[`${branch} Incidents`],
					['Category', 'Branch Total', 'Total', 'Percentage'],
				];

				for (const category in branchData) {
					if (branchData.hasOwnProperty(category)) {
						const incidents = branchData[category];
						const categoryTotal = totalIncidentsPerCategory[category] || 0;
						const percentage =
							((incidents / categoryTotal) * 100).toFixed(2) + '%';
						sheetData.push([
							category,
							String(incidents),
							String(categoryTotal),
							percentage,
						]);
					}
				}

				const sheet = XLSX.utils.aoa_to_sheet(sheetData);
				XLSX.utils.book_append_sheet(workbook, sheet, branch);
			}
		}

		XLSX.writeFile(workbook, 'report.xlsx');
	};

	const clickPage = (page: number) => {
		if (page < 1 || page > pagination.pageCount || page === currentPage) {
			return;
		}

		setFilters(filters, page, pageSize);
	};

	const changePageSize = (nextPageSize: number) => {
		setFilters(filters, 1, nextPageSize);
	};

	const renderPageNumbers = () => {
		if (pagination.pageCount <= 1) {
			return [];
		}

		const maxVisiblePages = 7;
		let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
		let endPage = Math.min(
			pagination.pageCount,
			startPage + maxVisiblePages - 1
		);

		if (endPage - startPage + 1 < maxVisiblePages) {
			startPage = Math.max(1, endPage - maxVisiblePages + 1);
		}

		const pages: number[] = [];
		for (let page = startPage; page <= endPage; page += 1) {
			pages.push(page);
		}

		return pages;
	};

	const getIncidentsWrapped = (filters: string, prepend: boolean = false) => {
		const searchParams = new URLSearchParams(filters || '');
		searchParams.delete('prepend');

		if (!searchParams.get('page')) {
			searchParams.set('page', String(currentPage));
		}

		if (!searchParams.get('pageSize')) {
			searchParams.set('pageSize', String(pageSize));
		}

		if (prepend) {
			searchParams.set('prepend', 'true');
		}

		getIncidentsCloseModal(searchParams.toString());
	};

	const spliceFromArrayById = (array: any[], id: string | number) =>
		array.filter((item) => item.id !== id);

	const removeIdFromList = (
		id: string | number,
		isNuisanceOrDuplicate: boolean
	) => {
		if (
			!includeNuisanceInCsv() &&
			!includeDuplicateInCsv() &&
			period === 'all' &&
			isNuisanceOrDuplicate
		) {
			setCurrentTableData(spliceFromArrayById(currentTableData, id));
			toast.success(
				'Nuisance/duplicate incident removed from listing successfully.'
			);
		} else if (period !== 'all') {
			setCurrentTableData(spliceFromArrayById(currentTableData, id));
			toast.success('Incident closed.');
		} else {
			toast.success('Incident closed.');
		}
	};

	return (
		<MainLayoutV2
			url="incidents"
			query={query.get('view') ? `?view=${query.get('view')}` : undefined}
		>
			<main className="flex h-full">
				<InPageSearch open={open} setOpen={setOpen} searchType={'incident'}>
					<IncidentSearchBar setFilters={setFilters} setPeriod={setPeriod} />
				</InPageSearch>
				<div className="flex-auto overflow-y-scroll">
					<div className="flex items-start justify-between pt-7 px-4 sticky top-0 z-30 bg-white border-t-[1px] 2xl:border-b-secondary-100 border-solid 2xl:border-[1px]">
						<div className="flex flex-col justify-start">
							<Button
								type="secondary"
								onClick={handleOpen}
								className={`${
									open ? 'invisible' : ''
								} text-[14px] tracking-[1.92px] !rounded-3xl`}
							>
								Search By
								<span className="ml-[4px]">
									<SideBarIcon color="#FAA431" />
								</span>
							</Button>
						</div>
						<div className="hidden 2xl:block">
							<div className="flex items-center justify-center flex-1 w-full space-x-4">
								{hasAccess(userPermission, 'admin-manager') && (
									<TabLink
										onClick={() => {
											setFilters('period=today&status=new');
											setPeriod('today');
										}}
										active={period === 'today'}
										v2={true}
									>
										Process area
									</TabLink>
								)}
								<TabLink
									onClick={() => {
										setFilters('period=week&status=open');
										setPeriod('week');
									}}
									active={period === 'week'}
									v2={true}
								>
									Open reports
								</TabLink>
								<TabLink
									onClick={() => {
										setFilters(
											`period=month&status=open&assignedUserId=${userId}`
										);
										setPeriod('month');
									}}
									active={period === 'month'}
									v2={true}
								>
									Open actions assigned to me
								</TabLink>
								<TabLink
									onClick={() => {
										setFilters('period=all');
										setPeriod('all');
									}}
									active={period === 'all'}
									v2={true}
								>
									All
								</TabLink>
							</div>
						</div>
						<div className="flex flex-row">
							<Button
								onClick={exportToExcel}
								className="mr-4 tracking-[1.92px] !rounded-3xl"
								type="quaternary"
							>
								<span className="mr-[4px]">
									<ExportIcon />
								</span>
								Export XLSX
							</Button>
							<Button
								onClick={exportToCSV}
								isDisabled={isCsvExporting}
								className="mr-4 tracking-[1.92px] !rounded-3xl"
								type="quaternary"
							>
								<span className="mr-[4px]">
									<ExportIcon />
								</span>
								{isCsvExporting ? 'Exporting CSV...' : 'Export CSV'}
							</Button>
							<Button
								onClick={createNewIncident}
								className="text-[14px] tracking-[1.92px] !rounded-3xl"
							>
								<PlusIcon className="h-5 w-5 mr-1" />
								New Report
							</Button>
						</div>
					</div>
					<div className="2xl:hidden mt-[20px] border-solid border-b-[1px] border-secondary-200">
						<div className="flex items-center justify-center flex-1 w-full space-x-4">
							{hasAccess(userPermission, 'admin-manager') && (
								<TabLink
									onClick={() => {
										setFilters('period=today&status=new');
										setPeriod('today');
									}}
									active={period === 'today'}
									v2={true}
								>
									Process area
								</TabLink>
							)}
							<TabLink
								onClick={() => {
									setFilters('period=week&status=open');
									setPeriod('week');
								}}
								active={period === 'week'}
								v2={true}
							>
								Open reports
							</TabLink>
							<TabLink
								onClick={() => {
									setFilters(
										`period=month&status=open&assignedUserId=${userId}`
									);
									setPeriod('month');
								}}
								active={period === 'month'}
								v2={true}
							>
								Open actions assigned to me
							</TabLink>
							<TabLink
								onClick={() => {
									setFilters('period=all');
									setPeriod('all');
								}}
								active={period === 'all'}
								v2={true}
							>
								All
							</TabLink>
						</div>
					</div>
					<Table
						headings={headings}
						data={currentTableData}
						refetchData={() => getIncidents(filters)}
						splitAssignedToMe={false}
						hideColumns={false}
						removeIdFromList={removeIdFromList}
					/>
					{currentTableData.length > 0 && (
						<div className="text-center text-sm text-gray-600 mt-2 mb-4">
							Showing {currentTableData.length} of {pagination.total} incident
							{pagination.total !== 1 ? 's' : ''}
						</div>
					)}
					{currentTableData.length === 0 && isListLoading && (
						<div className="text-center text-sm text-gray-600 mt-8 mb-4">
							Loading incidents...
						</div>
					)}
					{currentTableData.length === 0 && !isListLoading && (
						<div className="text-center text-sm text-gray-600 mt-8 mb-4">
							No incidents found
						</div>
					)}
					<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4 mb-4 px-4">
						<div className="flex items-center gap-2 text-sm text-gray-600">
							<label htmlFor="incidents-page-size">Items per page</label>
							<select
								id="incidents-page-size"
								className="border border-secondary-200 rounded pl-2 pr-10 py-1 bg-white"
								value={pageSize}
								onChange={(event) => changePageSize(Number(event.target.value))}
							>
								{pageSizeOptions.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>
						<div className="flex items-center justify-center gap-2 flex-wrap">
							<Button
								type="secondary"
								onClick={() => clickPage(currentPage - 1)}
								isDisabled={isListLoading || currentPage <= 1}
							>
								Prev
							</Button>
							{renderPageNumbers().map((pageNumber) => (
								<Button
									key={pageNumber}
									type={pageNumber === currentPage ? 'primary' : 'secondary'}
									onClick={() => clickPage(pageNumber)}
									isDisabled={isListLoading}
								>
									{pageNumber}
								</Button>
							))}
							<Button
								type="secondary"
								onClick={() => clickPage(currentPage + 1)}
								isDisabled={
									isListLoading || currentPage >= pagination.pageCount
								}
							>
								Next
							</Button>
						</div>
						<div className="text-sm text-gray-600 text-right">
							Page {pagination.pageCount === 0 ? 0 : currentPage} of{' '}
							{pagination.pageCount}
						</div>
					</div>
				</div>
			</main>
			<SlideOver
				open={IncidentState.commentsOpen}
				onClose={handleCloseComments}
			>
				<CommentPanel
					id={IncidentState.commentIncidentId}
					onClose={handleCloseComments}
					createActivity={(payload) => createIncidentActivity(payload)}
					updateActivity={updateIncidentActivity}
					deleteActivity={deleteIncidentActivity}
					activityData={activityData}
					getActivity={getIncidentActivity}
					activityType="incident"
					hasPermissionAccess={hasManagerAccess}
				/>
			</SlideOver>
			<SlideOver
				open={IncidentState.incidentReportOpen}
				onClose={handleCloseIncidentReport}
			>
				<IncidentPanel
					getIncidents={getIncidentsWrapped}
					filters={filters}
					onClose={handleCloseIncidentReport}
				/>
			</SlideOver>
		</MainLayoutV2>
	);
};

export default Incidents;
